import {
    CREATION_STAGE_ORDER,
    CreationStage,
} from 'js/src/data/agent-activity/agent-activity-types';
import uuid from 'js/src/libs/uuid';

/**
 * Generated once per page load, when this module is first evaluated. Events that
 * describe one pass through a flow — the agent's creation-stage events and the
 * interactable event, say — carry it so they can be correlated with each other
 * without also collapsing separate page loads into one group.
 */
const ANALYTICS_SESSION_ID = uuid();

export function getAnalyticsSessionId(): string {
    return ANALYTICS_SESSION_ID;
}

const STAGE_DURATION_PROPERTY: Record<CreationStage, string> = {
    [CreationStage.PLANNING]: 'planning_time_ms',
    [CreationStage.BUILDING]: 'building_time_ms',
    [CreationStage.REVIEWING]: 'reviewing_time_ms',
};

/**
 * When each stage of the current build began. Kept here rather than in Redux because
 * nothing renders it: it exists only so a stage event can report how long the stage
 * before it took. It outlives the Redux creation stage, which is cleared at the end of
 * every turn, so the interactable event can still read it once the video is on screen.
 */
let stageStartedAt: Partial<Record<CreationStage, number>> = {};

/** A stage's start is recorded once — re-announcements must not restart the clock. */
export function recordStageStart(stage: CreationStage): void {
    if (stageStartedAt[stage] === undefined) {
        stageStartedAt[stage] = Date.now();
    }
}

/**
 * Durations of the stages that have already finished, i.e. every stage whose
 * successor has started. `includeElapsedStage` also measures the stage still
 * underway up to now, which is what the interactable event wants: by the time the
 * video is on screen the review is effectively over, but nothing has announced a
 * stage after it to close the bracket.
 */
export function getStageDurationProperties({
    includeElapsedStage = false,
}: { includeElapsedStage?: boolean } = {}): Record<string, number> {
    const startedStages = CREATION_STAGE_ORDER.filter(
        (stage) => stageStartedAt[stage] !== undefined,
    );

    const properties: Record<string, number> = {};
    startedStages.forEach((stage, index) => {
        const startedAt = stageStartedAt[stage] as number;
        const endedAt = startedStages[index + 1]
            ? (stageStartedAt[startedStages[index + 1]] as number)
            : includeElapsedStage
              ? Date.now()
              : undefined;
        if (endedAt !== undefined) {
            properties[STAGE_DURATION_PROPERTY[stage]] = endedAt - startedAt;
        }
    });
    return properties;
}

/** Called when a fresh build begins, so a retry on the same page isn't timed from the earlier attempt. */
export function resetStageTimings(): void {
    stageStartedAt = {};
}
